import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Lock } from "lucide-react";
import Nav from "../site/Nav";
import Footer from "../site/Footer";
import IosInstallPrompt from "../site/IosInstallPrompt";
import { toast } from "sonner";
import { TOKEN_KEY, fmtDate, fmtMoney } from "../admin/adminUtils";
import { Badge } from "../admin/IntakeStat";
import AiBriefPanel from "../admin/AiBriefPanel";
import IntakeDetailFields from "../admin/IntakeDetailFields";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminIntakeDetailPage() {
  const { briefId } = useParams();
  const navigate = useNavigate();
  const [token, setToken] = useState(() =>
    typeof window === "undefined" ? "" : window.sessionStorage.getItem(TOKEN_KEY) || "",
  );
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [genLoading, setGenLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const fetchDoc = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/admin/website-intake-submissions/${briefId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDoc(res.data);
    } catch (err) {
      const s = err?.response?.status;
      if (s === 401 || s === 403) {
        window.sessionStorage.removeItem(TOKEN_KEY);
        setToken("");
        setError("Token rejected. Go back to /admin/intakes and unlock again.");
      } else if (s === 404) {
        setError("Submission not found.");
      } else {
        setError(err?.response?.data?.detail || "Could not load submission.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate("/admin/intakes");
      return;
    }
    fetchDoc();
    // `fetchDoc` is a closure over token/briefId — both already in deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, briefId]);

  const generate = async () => {
    setGenLoading(true);
    try {
      const res = await axios.post(
        `${API}/admin/website-intake-submissions/${briefId}/generate-brief`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setDoc((prev) => ({ ...prev, ...res.data }));
      toast.success("Kickoff brief generated");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not generate brief.");
    } finally {
      setGenLoading(false);
    }
  };

  const startEdit = () => {
    setDraft(doc?.ai_brief || "");
    setEditing(true);
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      const res = await axios.put(
        `${API}/admin/website-intake-submissions/${briefId}/ai-brief`,
        { ai_brief: draft },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setDoc((prev) => ({ ...prev, ...res.data }));
      setEditing(false);
      toast.success("Edits saved");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not save edits.");
    } finally {
      setSavingEdit(false);
    }
  };

  const copyBrief = async () => {
    try {
      await navigator.clipboard.writeText(doc?.ai_brief || "");
      toast.success("Brief copied to clipboard");
    } catch {
      toast.error("Clipboard unavailable");
    }
  };

  if (!token) return null;

  if (loading) {
    return (
      <div className="bg-sand-100 min-h-screen" data-testid="admin-intake-detail-loading">
        <Nav />
        <main className="pt-28 pb-20 px-6">
          <p className="text-center text-ink-muted text-[14px]">Loading submission…</p>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-sand-100 min-h-screen">
        <Nav />
        <main className="pt-28 pb-20 px-6">
          <div className="max-w-md mx-auto rounded-3xl border border-sand-300 bg-sand-50 p-8">
            <span className="inline-flex h-11 w-11 grid place-items-center rounded-full bg-rust/10 text-rust border border-rust/30">
              <Lock size={18} />
            </span>
            <p className="mt-6 text-[14px] text-ink">{error}</p>
            <Link to="/admin/intakes" className="mt-6 inline-flex items-center gap-2 text-clay text-[13px] hover:text-rust">
              <ArrowLeft size={14} /> Back to intakes
            </Link>
          </div>
        </main>
        <Footer />
        <IosInstallPrompt />
      </div>
    );
  }

  return (
    <div className="bg-sand-100 text-ink min-h-screen" data-testid="admin-intake-detail-page">
      <Nav />
      <main className="pt-24 sm:pt-28 md:pt-32 pb-20 px-5 sm:px-6 md:px-10">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <Link
                to="/admin/intakes"
                data-testid="admin-detail-back"
                className="inline-flex items-center gap-2 font-mono text-[10.5px] uppercase tracking-[0.18em] text-clay hover:text-rust"
              >
                <ArrowLeft size={12} /> Back to intakes
              </Link>
              <h1 className="font-display mt-3 text-[1.9rem] sm:text-[2.3rem] md:text-[2.7rem] leading-[1.05] tracking-[-0.02em] font-semibold text-ink">
                {doc?.business_name || "—"}
              </h1>
              <p className="mt-2 text-[14px] text-ink-muted">
                {doc?.full_name || "—"} · {doc?.email || "—"} · {doc?.phone || "—"}
              </p>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <Badge status={doc?.status} />
                <span className="font-mono text-[10.5px] uppercase tracking-[0.16em] text-ink-muted">
                  Submitted {fmtDate(doc?.created_at)}
                </span>
                {doc?.paid_at && (
                  <span className="font-mono text-[10.5px] uppercase tracking-[0.16em] text-olive">
                    · Paid {fmtDate(doc?.paid_at)} · {fmtMoney(doc?.stripe_amount_total, doc?.stripe_currency)}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* 2-col layout: AI brief (primary) + form fields (secondary) */}
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-8">
            <AiBriefPanel
              doc={doc}
              editing={editing}
              draft={draft}
              setDraft={setDraft}
              genLoading={genLoading}
              savingEdit={savingEdit}
              onGenerate={generate}
              onStartEdit={startEdit}
              onSaveEdit={saveEdit}
              onCancelEdit={() => setEditing(false)}
              onCopy={copyBrief}
            />
            <IntakeDetailFields doc={doc} />
          </div>
        </div>
      </main>
      <Footer />
      <IosInstallPrompt />
    </div>
  );
}
